import { useState } from 'react';

const PHONE = '561-445-3948';
const FORM_URL = 'https://readdy.ai/api/form/d70pu4f9hgr6oot2r4c0';

const serviceOptions = [
  'Pressure Cleaning',
  'Roof Cleaning',
  'Driveway Cleaning',
  'Screen Patio Cleaning',
  'Gutter Cleaning',
  'Multiple Services',
];

export default function FinalCTA() {
  const [formData, setFormData] = useState({ name: '', phone: '', service: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service) return;
    setStatus('submitting');
    try {
      const body = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        service: formData.service,
      });
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', service: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="relative overflow-hidden bg-orange-500 py-16 lg:py-24">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5">
        <img
          src="https://public.readdy.ai/ai/img_res/02620270a4f7ba1a05d68bc24c9da517.jpg"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="inline-block bg-white/20 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
            Don't Wait
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-4 leading-tight">
            Spots Fill Fast — Get Your{' '}
            <span className="text-orange-900">Free Estimate Now</span>
          </h2>
          <p className="text-orange-100 text-lg max-w-xl mx-auto mb-8">
            Join 500+ satisfied South Florida homeowners. One call is all it takes to transform your property.
          </p>
          <a
            href={`tel:${PHONE}`}
            className="inline-flex items-center gap-3 bg-white hover:bg-orange-50 text-orange-600 font-black text-2xl lg:text-3xl px-8 py-5 rounded-xl transition-colors cursor-pointer whitespace-nowrap mb-6"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-phone-fill text-2xl text-orange-500"></i>
            </div>
            {PHONE}
          </a>
          <p className="text-orange-200 text-sm">Or fill out the quick form below ↓</p>
        </div>

        {/* Form */}
        <div className="max-w-2xl mx-auto">
          {status === 'success' ? (
            <div className="bg-white rounded-2xl p-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal-100 mx-auto mb-4">
                <i className="ri-check-double-line text-teal-600 text-2xl"></i>
              </div>
              <h3 className="text-slate-900 font-black text-xl mb-2">We Got Your Request!</h3>
              <p className="text-slate-500 text-sm">We'll call you back shortly. Expect to hear from us very soon!</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="final-estimate-form"
              className="bg-white rounded-2xl p-6 lg:p-8"
            >
              <h3 className="text-slate-900 font-black text-xl mb-5 text-center">Quick Free Estimate Form</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                <input
                  name="name"
                  type="text"
                  required
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-teal-500 placeholder:text-slate-400"
                />
                <input
                  name="phone"
                  type="tel"
                  required
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-teal-500 placeholder:text-slate-400"
                />
                <select
                  name="service"
                  required
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="border border-gray-300 rounded-lg px-4 py-3 text-sm text-slate-800 focus:outline-none focus:border-teal-500"
                >
                  <option value="">Service Needed...</option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              {status === 'error' && (
                <p className="text-red-600 text-xs mb-2 text-center">Error submitting. Please call us directly!</p>
              )}
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-black text-base py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {status === 'submitting' ? 'Sending...' : 'Get My Free Estimate Now →'}
              </button>
              <p className="text-slate-400 text-xs text-center mt-3">No commitment. Fast response. 100% free.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
